"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@garagem/ui/components/sonner";
import { authQueryKeys } from "./query-keys";
import type { BetterAuthRoleType } from "@/db/auth/permissions";
import { getUserFriendlyErrorMessage } from "./parse-auth-error";
import { useTRPC } from "@/lib/trpc/client";
import { garagemAuth } from "@/db/auth/client";

/**
 * @deprecated Prefer `auth.deactivateMember` (usuarios:excluir). Native Better
 * Auth `organization.removeMember` is ungated and must not be used from the UI.
 */
export function useRemoveMember() {
  return useMutation({
    mutationFn: async (_args: {
      memberIdOrEmail: string;
      organizationId: string;
    }) => {
      throw new Error(
        "useRemoveMember is disabled — use auth.deactivateMember (usuarios:excluir) instead"
      );
    },
  });
}

/**
 * Update a member's organization role via the gated garagem-auth endpoint
 * (`usuarios:editar`, non-bypassing). Replaces Better Auth's native
 * `organization.updateMemberRole`, which had no granular permission check.
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const utils = trpc.useUtils();

  return useMutation({
    mutationFn: async ({
      memberId,
      role,
    }: {
      memberId: string;
      role: BetterAuthRoleType | string;
    }) => {
      if (!garagemAuth?.customRoles?.updateMemberRoleGated) {
        throw new Error("Gated updateMemberRole API not available");
      }
      const response = await garagemAuth.customRoles.updateMemberRoleGated({
        memberId,
        role,
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast.success("Papel atualizado com sucesso!");
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.all,
      });
      utils.auth.getTeamsWithMembers.invalidate();
    },
    onError: (error) => {
      const errorMessage = getUserFriendlyErrorMessage(error);
      toast.error(errorMessage.title, {
        description: errorMessage.description,
      });
    },
  });
}
